
///
/// EarthBrowser
///
/// Created by Matt Giger on 12/20/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Button } from "@/components/ui/button"
import { setProfileOpen, setSubscribeOpen } from "@/redux/dialogSlice"
import { useAppDispatch } from "@/redux/store"
import { useGetUserSubscriptionQuery } from "@/redux/userApi"
import { PricingPlans } from "@shared-types/pricing-plans"
import type { Subscription } from "@shared-types/subscription"
import { SparklesIcon } from "lucide-react"
import { SubscriptionCardActive } from "./SubscriptionCardActive"
import { SubscriptionCardAdSupported } from "./SubscriptionCardAdSupported"
import { SubscriptionCardCancelled } from "./SubscriptionCardCancelled"

export function SubscriptionSection() {
	const dispatch = useAppDispatch()
	const { data: userSubscription, isLoading, isError } = useGetUserSubscriptionQuery()

	function handleSubscribe() {
		dispatch(setProfileOpen(false))
		dispatch(setSubscribeOpen(true))
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-4">
				<div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
			</div>
		)
	}

	if (isError || !userSubscription) {
		return (
			<div className="rounded-lg border border-dashed border-border p-4 flex items-center justify-center">
				<p className="text-sm text-muted-foreground italic">
					Unable to load subscription data
				</p>
			</div>
		)
	}

	const orderedSubscriptions = userSubscription
		.concat()
		.sort((a, b) => {
			return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
		})

	if (orderedSubscriptions.length === 0) {
		const plan = PricingPlans.yearly
		return (
			<div className="rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 p-6 flex flex-col items-center justify-center text-center">
				<div className="p-3 rounded-full bg-amber-500/10 mb-3">
					<SparklesIcon className="h-6 w-6 text-amber-500" />
				</div>
				<h3 className="text-base font-semibold text-foreground mb-1">No active subscription</h3>
				<p className="text-sm text-muted-foreground mb-4 max-w-sm">
					Unlock 10-day forecasts, 48-hour radar history, and up to 5 devices with {plan.name}.
				</p>
				<Button
					onClick={handleSubscribe}
					className="rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
				>
					<SparklesIcon className="h-4 w-4 mr-2" />
					Subscribe &mdash; ${plan.price.toFixed(2)}/{plan.interval}
				</Button>
			</div>
		)
	}

	return (
		<div className="space-y-3">
			{orderedSubscriptions.map((subscription) => (
				<SubscriptionCard
					key={`${subscription.subscription_id}-${subscription.created_at}`}
					subscription={subscription}
				/>
			))}
		</div>
	)
}

function SubscriptionCard({ subscription }: { subscription: Subscription }) {
	if (subscription.subscription_id === "free_trial" || subscription.subscription_id === "ad_supported") {
		return <SubscriptionCardAdSupported key={subscription.subscription_id} subscription={subscription} />
	}
	switch (subscription.status) {
		case "cancelled":
			return <SubscriptionCardCancelled subscription={subscription} />
		case "active":
		case "trialing": // only if a trial A/B is enabled; renders like active
			return <SubscriptionCardActive subscription={subscription} />
		default:
			return null
	}
}
